import {
  SubscriptionResponse,
  SubscriptionView,
  toView,
} from "../../../models/view/subscription/subscription.view";
import { inject, signal } from "@angular/core";
import {
  ExternalCollection,
  FetchParams,
} from "@bitwarden/web-vault/app/services/store/external.collection.abstraction";
import { StateService } from "@bitwarden/web-vault/app/core";

interface SubscriptionFetchParams extends FetchParams {
  userId: string;
}

export class ExternalSubscriptionStoreCollection extends ExternalCollection<
  SubscriptionView,
  SubscriptionFetchParams
> {
  private legacyAppState = inject(StateService);

  private _activeSubscription = signal<SubscriptionView>(null);
  readonly activeSubscription = this._activeSubscription.asReadonly();

  /**
   * False until subscription status has been determined for this session (from the login JWT
   * or a gloss-api getActive override). `activeSubscription()` is null both before load and for
   * a genuinely-free user, so "not premium" UI must gate on this to avoid flashing while the
   * real bundle/subscription status is still resolving (#14137).
   */
  private _isResolved = signal(false);
  readonly isResolved = this._isResolved.asReadonly();

  clear(): void {
    this._activeSubscription.set(null);
    this._isResolved.set(false);
  }

  // Nothing to fetch: the active subscription comes from the token or gloss-api, and the history
  // is ExternalUserCollection.subscriptions. Required by ExternalCollection.
  fetch(params: SubscriptionFetchParams): Promise<SubscriptionView> {
    return Promise.resolve(undefined);
  }

  protected mapToView(data: SubscriptionResponse): SubscriptionView {
    return this.deepFreeze(toView(data));
  }

  /** Apply gloss-api getActive data (overrides stale JWT subscription for UI). */
  applyFromGlossApi(data: SubscriptionResponse): void {
    this._activeSubscription.set(this.mapToView(data));
    this._isResolved.set(true);
    void this.legacyAppState.setSubscription(data);
  }

  triggerChanges(): void {
    /** Load legacy token active subscription **/
    this.legacyAppState
      .getSubscription()
      .then((tokenSubscription: SubscriptionResponse) => {
        if (tokenSubscription) {
          this._activeSubscription.set(this.mapToView(tokenSubscription));
        }
      })
      .finally(() => {
        // Runs on both success and failure: status is marked resolved either way so
        // "not premium" UI/sync is never stranded suppressed forever (#14137). A failure
        // here leaves activeSubscription null (treated as free) and surfaces as an
        // unhandled rejection rather than being silently swallowed.
        this._isResolved.set(true);
      });
    // The subscription history is ExternalUserCollection.subscriptions.
  }
}
